<script setup>
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { formatCsatScore } from 'dashboard/helper/csatScore';

const props = defineProps({
  questions: { type: Array, default: () => [] },
});

const { t } = useI18n();

const hasQuestions = computed(() => props.questions.length > 0);

// Chaves literais (o lint do projeto barra chave montada) e tipo desconhecido
// cai em string vazia, não numa tradução inexistente.
const typeLabel = question => {
  if (question.type === 'scale') return t('CSAT_REPORTS.QUESTIONS.TYPE.SCALE');
  if (question.type === 'choice')
    return t('CSAT_REPORTS.QUESTIONS.TYPE.CHOICE');
  if (question.type === 'text') return t('CSAT_REPORTS.QUESTIONS.TYPE.TEXT');

  return '';
};

// A nota na régua da pergunta (helper/csatScore: mesma escrita do topo).
const score = question =>
  formatCsatScore(
    {
      kind: question.summary?.kind,
      value: question.summary?.value,
      max: question.scale_max,
    },
    t
  );

const hasDescriptions = question =>
  question.distribution.some(point => point.description);

const percentOf = (question, point) =>
  question.count ? Math.round((point.count * 100) / question.count) : 0;

// Cada caixa tem a sua pesquisa: com mais de uma no período, a linha diz de
// qual caixa é a pergunta (a `pergunta_1` de uma não é a de outra).
const severalInboxes = computed(
  () => new Set(props.questions.map(question => question.inbox_id)).size > 1
);

const metaText = question =>
  [
    severalInboxes.value ? question.inbox_name : null,
    typeLabel(question),
    t('CSAT_REPORTS.QUESTIONS.ANSWERS', question.count, {
      count: question.count,
    }),
  ]
    .filter(Boolean)
    .join(' · ');

const pointText = (question, point) =>
  `${point.count} · ${percentOf(question, point)}%`;

// Cada pergunta começa recolhida numa linha (texto, régua, nota, respostas);
// o gráfico abre ao clicar. Aberto por padrão, 10 barras por pergunta viravam
// uma parede que empurrava a tabela para longe.
const blockKey = question =>
  `${question.inbox_id}-${question.key}-${question.scale_min}-${question.scale_max}`;
const expanded = ref(new Set());
const isExpanded = question => expanded.value.has(blockKey(question));
const toggle = question => {
  const next = new Set(expanded.value);
  const key = blockKey(question);
  if (next.has(key)) next.delete(key);
  else next.add(key);
  expanded.value = next;
};
</script>

<template>
  <div
    class="flex flex-col gap-3 p-6 border rounded-xl border-n-weak bg-n-solid-1"
  >
    <div class="flex flex-col gap-1">
      <h3 class="text-base font-medium text-n-slate-12">
        {{ $t('CSAT_REPORTS.QUESTIONS.TITLE') }}
      </h3>
      <p class="text-sm text-n-slate-11">
        {{ $t('CSAT_REPORTS.QUESTIONS.DESCRIPTION') }}
      </p>
    </div>

    <p v-if="!hasQuestions" class="text-sm text-n-slate-11">
      {{ $t('CSAT_REPORTS.QUESTIONS.EMPTY') }}
    </p>

    <template v-else>
      <div
        v-for="question in questions"
        :key="blockKey(question)"
        class="flex flex-col gap-3 pt-3 border-t border-n-weak"
      >
        <button
          type="button"
          class="flex items-center justify-between w-full gap-4 text-left"
          :aria-expanded="isExpanded(question)"
          @click="toggle(question)"
        >
          <div class="flex flex-col min-w-0 gap-0.5">
            <span class="text-sm font-medium truncate text-n-slate-12">
              {{ question.text }}
            </span>
            <span class="text-xs text-n-slate-11">
              {{ metaText(question) }}
            </span>
          </div>
          <div class="flex items-center gap-3 shrink-0">
            <div v-if="question.summary" class="flex flex-col items-end">
              <span class="tabular-nums text-n-slate-12">
                <span class="text-base font-medium">
                  {{ score(question).main }}
                </span>
                <span class="text-xs text-n-slate-11">
                  {{ score(question).unit }}
                </span>
              </span>
              <span class="text-xs text-n-slate-11">
                {{ score(question).hint }}
              </span>
            </div>
            <span
              class="size-4 text-n-slate-11 transition-transform i-lucide-chevron-down"
              :class="{ 'rotate-180': isExpanded(question) }"
            />
          </div>
        </button>

        <div
          v-if="isExpanded(question) && question.distribution.length"
          class="flex flex-col gap-1.5"
        >
          <div
            v-for="point in question.distribution"
            :key="point.value"
            class="flex items-center gap-3"
          >
            <span
              class="text-xs text-right truncate text-n-slate-11"
              :class="hasDescriptions(question) ? 'w-40' : 'w-16'"
            >
              <!-- Com legenda: "10 · Excelente" (a que o cliente viu na lista). -->
              <span class="font-medium text-n-slate-12">{{ point.label }}</span>
              <template v-if="point.description">
                · {{ point.description }}
              </template>
            </span>
            <div class="relative flex-1 h-2 rounded-full bg-n-slate-3">
              <!-- Uma cor só, de propósito: pintar faixas por "bom/ruim" exigiria
                 uma classificação que não existe em escala arbitrária. -->
              <div
                class="absolute inset-y-0 left-0 w-[var(--bar-width)] rounded-full bg-n-blue-9"
                :style="{ '--bar-width': `${percentOf(question, point)}%` }"
              />
            </div>
            <span class="w-20 text-xs tabular-nums text-n-slate-11">
              {{ pointText(question, point) }}
            </span>
          </div>
        </div>
      </div>
    </template>
  </div>
</template>
